/**
 * ClientWorkspaceReducer — local state management for ClientWorkspaceContent.
 *
 * Manages files, folders, pagination, loading, and error states.
 * Used via useReducer inside ClientWorkspaceContent.
 */

import {
  ClientWorkspaceFile,
  ClientWorkspaceFolder,
  ClientWorkspaceReducerAction,
  ClientWorkspaceReducerActionPayload,
  ClientWorkspaceState,
} from "./types";

export const INITIAL_WORKSPACE_STATE: ClientWorkspaceState = {
  files: [],
  folders: [],
  pagination: {
    current_page: 1,
    per_page: 50,
    total: 0,
    last_page: 1,
  },
  isLoadingFiles: false,
  isLoadingFolders: false,
  filesError: null,
  foldersError: null,
};

export function clientWorkspaceReducer(
  state: ClientWorkspaceState,
  action: ClientWorkspaceReducerActionPayload,
): ClientWorkspaceState {
  switch (action.type) {
    case ClientWorkspaceReducerAction.SetFiles:
      return { ...state, files: (action.payload as ClientWorkspaceFile[]) ?? [], filesError: null };

    case ClientWorkspaceReducerAction.AppendFiles: {
      const newFiles = (action.payload as ClientWorkspaceFile[]) ?? [];
      return { ...state, files: [...state.files, ...newFiles] };
    }

    case ClientWorkspaceReducerAction.RemoveFile: {
      const id = action.payload as number;
      return { ...state, files: state.files.filter((f) => f.id !== id) };
    }

    case ClientWorkspaceReducerAction.UpdateFileInList: {
      const { id, changes } = action.payload as { id: number; changes: Partial<ClientWorkspaceFile> };
      return {
        ...state,
        files: state.files.map((f) => (f.id === id ? { ...f, ...changes } : f)),
      };
    }

    case ClientWorkspaceReducerAction.SetFolders:
      return { ...state, folders: (action.payload as ClientWorkspaceFolder[]) ?? [], foldersError: null };

    case ClientWorkspaceReducerAction.AddFolder: {
      const folder = action.payload as ClientWorkspaceFolder;
      return { ...state, folders: [...state.folders, folder] };
    }

    case ClientWorkspaceReducerAction.RemoveFolder: {
      const id = action.payload as number;
      return { ...state, folders: state.folders.filter((f) => f.id !== id) };
    }

    case ClientWorkspaceReducerAction.UpdateFolderInList: {
      const { id: folderId, changes } = action.payload as {
        id: number;
        changes: Partial<ClientWorkspaceFolder>;
      };
      return {
        ...state,
        folders: state.folders.map((f) => (f.id === folderId ? { ...f, ...changes } : f)),
      };
    }

    case ClientWorkspaceReducerAction.SetLoadingFiles:
      return { ...state, isLoadingFiles: (action.payload as boolean) ?? false };

    case ClientWorkspaceReducerAction.SetLoadingFolders:
      return { ...state, isLoadingFolders: (action.payload as boolean) ?? false };

    case ClientWorkspaceReducerAction.SetFilesError:
      return { ...state, filesError: (action.payload as string) ?? null, isLoadingFiles: false };

    case ClientWorkspaceReducerAction.SetFoldersError:
      return { ...state, foldersError: (action.payload as string) ?? null, isLoadingFolders: false };

    case ClientWorkspaceReducerAction.SetPagination:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...(action.payload as ClientWorkspaceState["pagination"]),
        },
      };

    case ClientWorkspaceReducerAction.ResetState:
      return { ...INITIAL_WORKSPACE_STATE };

    default:
      return state;
  }
}
